<template>
  <div>
    <div v-if="experienceError" class="alert alert-warning mb-4">
      <p class="m-0">{{ experienceError }}</p>

      <button
        v-if="experienceErrorStatus === 401"
        class="tb-btn tb-btn-contained high-em sm"
        style="margin-top: 0.5rem; display: block; margin-left: auto;"
        :disabled="reconnecting || !partnerInfo?.oauthUrl"
        @click="reconnect"
      >
        <span v-if="reconnecting" class="fas fa-spinner fa-spin mr-1"></span>
        Reconnect Account
      </button>

      <button
        v-else
        class="tb-btn tb-btn-text high-em sm"
        style="margin-top: 0.5rem; display: block; margin-left: auto;"
        @click="getExperience"
      >
        Retry
      </button>
    </div>

    <div v-if="triggers.length" class="action-key-container">
      <label class="tb-label">
        Triggers
      </label>

      <p>
        Trigger by pressing buttons or using keyboard shortcuts
      </p>

      <div class="action-keys">
        <action-key
          v-for="(trigger, index) in triggers"
          :key="index"
          :action="trigger"
          @trigger="onTrigger"
        />
      </div>
    </div>

    <div v-if="!isScorestream" class="tb-form-group">
      <label class="tb-label">
        URL
      </label>

      <input
        type="text"
        class="tb-input"
        placeholder="https://example.com"
        v-model="url"
        :class="{ 'is-invalid': url && !isValidUrl }"
        name="url"
        :disabled="isExperience"
        data-entity="graphics-frame-url"
        data-test="graphics-frame-url"
      />

      <span v-show="!isValidUrl" class="text-danger">Invalid url format</span>

      <small v-if="isExperience">
        Unable to update Browser Source URL when it is tied to an Experience.
      </small>
    </div>

    <template v-if="isScorestream">
      <div class="tb-form-group">
        <label class="tb-label">
          Widget Type
        </label>

        <select
          v-model="widgetType"
          class="tb-input"
          data-entity="graphics-frame-widgetType"
          data-test="graphics-frame-widgetType"
        >
          <option value="vert">
            Vertical Scoreboard
          </option>

          <option value="banner">
            Banner Scoreboard
          </option>

          <option value="teamSchedule">
            Team Schedule
          </option>
        </select>
      </div>

      <div class="tb-form-group">
        <label class="tb-label">
          Widget ID
        </label>

        <input
          v-model="widgetId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          class="tb-input"
          :class="{ 'is-invalid': widgetId && !isValidWidgetId }"
          data-entity="graphics-frame-widgetId"
          data-test="graphics-frame-widgetId"
          @input="validateNumericInput"
        />

        <span v-show="widgetId && !isValidWidgetId" class="text-danger">Widget ID must be a number</span>
      </div>
    </template>

    <div class="tb-form-group">
      <label class="tb-label">
        Background Color
      </label>

        <div class="mb-3 flex items-center">
        <div class="tb-radio mr-3">
          <input
            type="radio"
            id="solid"
            value="solid"
            v-model="fillStyle"
            data-entity="producer-editor-radioFillStyle"
            data-test="graphics-editor-fill-solid"
          />

          <label class="radio inline-radio" for="solid">Solid</label>
        </div>

        <div class="tb-radio">
          <input
            type="radio"
            id="gradient"
            value="gradient"
            v-model="fillStyle"
            data-entity="producer-editor-radioFillStyle"
            data-test="graphics-editor-fill-gradient"
          >

          <label class="radio inline-radio" for="gradient">Gradient</label>
        </div>
      </div>

      <template v-if="fillStyle === 'solid'">
        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker"
          data-entity="producer-editor-toggleBackgroundColorPicker"
          data-test="graphics-editor-background-color"
        >
          <colorpicker
            :color="backgroundColor"
            :large="true"
            show-buttons
            data-test="graphics-editor-background-color"
            @change="updateField('backgroundColor', $event)"
          />
        </div>
      </template>

      <template v-else>
        <div class="tb-form-group flex items-center">
          <div class="input-group">
            <input
              class="tb-input"
              type="number"
              name="gradientDirection"
              v-model.number="gradientDirection"
              data-entity="producer-editor-gradientDirection"
              data-test="graphics-editor-gradient-direction"
            />

            <span class="input-group-addon symbol">°</span>
          </div>

          <span class="ml-3" data-entity="producer-editor-toggleGradientColorOnePicker">
          <colorpicker
            :color="gradientColorOne"
            :large="true"
            show-buttons
            data-test="graphics-editor-gradient-color-one"
            @change="updateField('gradientColorOne', $event)"
          />
          </span>

          <span class="ml-3" data-entity="producer-editor-toggleGradientColorTwoPicker">
          <colorpicker
            :color="gradientColorTwo"
            :large="true"
            show-buttons
            data-test="graphics-editor-gradient-color-two"
            @change="updateField('gradientColorTwo', $event)"
          />
          </span>
        </div>
      </template>
    </div>

    <div class="tb-form-group">
      <div class="tb-checkbox">
        <input
          type="checkbox"
          id="backgroundImage"
          v-model="backgroundImage"
          data-entity="producer-editor-toggleBackgroundImage"
          data-test="graphics-editor-background-image"
        />

        <label for="backgroundImage">Background image</label>
      </div>
    </div>

    <div v-if="backgroundImage">
      <div class="tb-form-group">
        <div v-if="backgroundImageUrl" class="media-upload-wrapper">
          <div
            class="media"
            :style="{
                backgroundImage: `url('${backgroundImageUrl}')`,
              }"
          />

          <div class="edit-media">
            <button
              class="on-image rounded-[6px] border-0 bg-[#2bacca] p-2 text-white transition hover:bg-[#23cdf4]"
              data-entity="producer-editor-editBackgroundImage"
              data-test="graphics-editor-backgroundImage-select"
              @click="selectAsset"
            >
              <span class="fas fa-pencil-alt"></span>
            </button>
          </div>
        </div>

        <button
          v-else
          class="rounded-[6px] border-0 bg-[#2bacca] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#23cdf4]"
          data-entity="producer-editor-selectBackgroundImage"
          data-test="graphics-editor-backgroundImage-select"
          @click="selectAsset"
        >
          <span class="fas fa-image mr-1"></span>
          Choose image
        </button>
      </div>

      <div v-if="backgroundImageUrl" class="tb-form-group">
        <div class="tb-radio">
          <input
            type="radio"
            id="cover"
            value="cover"
            v-model="backgroundSize"
            data-entity="producer-editor-radioBackgroundSize"
            data-test="graphics-editor-backgroundSize-cover"
          />

          <!-- NOTE: Adding manual click handler because of weird scroll bug I can't fix yet -->
          <label class="radio inline-radio" for="cover" @click.prevent="backgroundSize = 'cover'">
            Fill image
          </label>
        </div>

        <div class="tb-radio">
          <input
            type="radio"
            id="contain"
            value="contain"
            v-model="backgroundSize"
            data-entity="producer-editor-radioBackgroundSize"
            data-test="graphics-editor-backgroundSize-fit"
          />

          <!-- NOTE: Adding manual click handler because of weird scroll bug I can't fix yet -->
          <label
            class="radio inline-radio"
            for="contain"
            @click.prevent="backgroundSize = 'contain'"
          >
            Fit image
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Partners from '../../../../services/partners';

import ActionKey from '../../../panel-editor/hotkeys/ActionKey.vue';

// Ordered list of keymaps for triggers on the PDK experience (if applicable).
// If triggers outnumber this list of chars, then subsequent triggers just won't
// have a hotkey.
const KeyMappings = '1234567890!@#$%^&*()-=_+';

export default {
  components: {
    ActionKey,
  },

  props: {
    graphic: {
      type: Object,
      required: true,
    },

    element: {
      type: Object,
      required: true,
    },

    graphic_fields: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      experience: null,
      experienceError: null,
      experienceErrorStatus: null,
      partnerInfo: null,
      reconnecting: false,
    };
  },

  mounted() {
    this.getExperience();
  },

  beforeUnmount() {
    window.removeEventListener('focus', this.onReconnectFocus);
  },

  computed: {
    triggers() {
      const hardcodedTriggers = [
        {
          type: 'frame-trigger',
          display: 'Refresh',
          hotkey: 'R',
          value: 'refresh',
        },
      ];

      const customTriggers = (this.experience?.triggers || []).map((trigger, idx) => ({
        type: 'frame-trigger',
        display: trigger.label,
        hotkey: KeyMappings[idx] || null,
        value: trigger.key,
      }));

      return [...hardcodedTriggers, ...customTriggers];
    },

    isExperience() {
      return !!this.element.experienceId;
    },

    isScorestream() {
      return this.element.isScorestream === true;
    },

    url: {
      get() {
        return this.elementFields?.url ?? this.element.url ?? '';
      },

      set(value) {
        this.updateField('url', value);
      },
    },

    widgetType: {
      get() {
        return this.elementFields?.widgetType ?? this.element.widgetType ?? 'vert';
      },

      set(value) {
        this.updateField('widgetType', value);
        this.updateScorestreamUrl();
      },
    },

    widgetId: {
      get() {
        return this.elementFields?.widgetId ?? this.element.widgetId ?? '';
      },

      set(value) {
        this.updateField('widgetId', value);
        this.updateScorestreamUrl();
      },
    },

    isValidWidgetId() {
      if (!this.widgetId) {
        return true;
      }

      return /^\d+$/.test(String(this.widgetId));
    },

    currentLayoutIndex() {
      return this.$store.state.production.production.index;
    },

    isCurrentLayout() {
      return this.graphic.timeline_id === this.currentLayoutIndex;
    },

    currentSampleLayoutIndex() {
      return this.$store.state.preview.production.index;
    },

    isCurrentSampleLayout() {
      return this.graphic.timeline_id === this.currentSampleLayoutIndex;
    },

    type() {
      return this.element.type;
    },

    backgroundColor() {
      return this.elementFields?.backgroundColor ?? this.element.backgroundColor ?? '#0F84FB';
    },

    gradientColorOne() {
      return this.elementFields?.gradientColorOne ?? this.element.gradientColorOne ?? '#00c4dd';
    },

    gradientColorTwo() {
      return this.elementFields?.gradientColorTwo ?? this.element.gradientColorTwo ?? '#0f84fb';
    },

    backgroundImage: {
      get() {
        return this.elementFields?.backgroundImage ?? this.element.backgroundImage ?? false;
      },

      set(value) {
        this.updateField('backgroundImage', value);
      },
    },

    backgroundSize: {
      get() {
        return this.elementFields?.backgroundSize ?? this.element.backgroundSize ?? 'cover';
      },

      set(value) {
        this.updateField('backgroundSize', value);
      },
    },

    backgroundImageUrl() {
      return this.elementFields?.backgroundImageUrl ?? this.element.backgroundImageUrl ?? '';
    },

    fillStyle: {
      get() {
        return this.elementFields?.fillStyle ?? this.element.fillStyle ?? 'solid';
      },

      set(value) {
        this.updateField('fillStyle', value);
      },
    },

    gradientDirection: {
      get() {
        // TODO: Replace magic number
        return this.elementFields?.gradientDirection ?? this.element.gradientDirection ?? 180;
      },

      set(value) {
        this.updateField('gradientDirection', value);
      },
    },

    elementFields() {
      return this.graphic_fields[this.element.id];
    },

    isValidUrl() {
      if (!this.url) {
        return true;
      }

      try {
        const urlObj = new URL(this.url);
        return urlObj.protocol === 'https:';
      } catch {
        return false;
      }
    },
  },

  methods: {
    async getExperience() {
      if (!this.element.experienceId) {
        return;
      }

      const { partner, experienceId } = this.element;
      this.experienceError = null;
      this.experienceErrorStatus = null;

      this.experience = await Partners.getExperience(partner, experienceId).catch((err) => {
        this.experienceErrorStatus = err.status || null;

        if (err.status === 401) {
          this.experienceError = 'Your session with this partner has expired. Please reconnect your account.';
        } else if (err.status) {
          this.experienceError = `Failed to load experience (${err.status}). Please try again later.`;
        } else {
          this.experienceError = 'Failed to load experience. Please try again later.';
          console.error('Failed to fetch Partner Experience:', err);
        }

        return null;
      });

      // Fetch partner info for reconnect (OAuth URL)
      if (partner && this.experienceErrorStatus === 401) {
        await this.fetchPartnerInfo(partner);
      }
    },

    async fetchPartnerInfo(partner) {
      try {
        const data = await Partners.getPartners();
        this.partnerInfo = (data?.partners || []).find((p) => p.slug === partner) || null;
      } catch (err) {
        console.error('Failed to fetch partner info:', err);
        this.partnerInfo = null;
      }
    },

    async reconnect() {
      if (!this.partnerInfo?.oauthUrl || this.reconnecting) {
        return;
      }

      this.reconnecting = true;

      window.open(
        this.partnerInfo.oauthUrl,
        '_blank',
        'popup=true,noopener,width=600,height=600',
      );

      // Listen for user returning after OAuth flow
      window.addEventListener('focus', this.onReconnectFocus);
    },

    async onReconnectFocus() {
      window.removeEventListener('focus', this.onReconnectFocus);

      this.reconnecting = false;
      this.experienceError = null;
      this.experienceErrorStatus = null;
      this.partnerInfo = null;

      // Re-fetch the experience with the new token
      await this.getExperience();
    },

    onTrigger(trigger) {
      const action = {
        timelineId: this.graphic.timeline_id,
        elementId: this.element.id,
        trigger,
      };

      if (this.isCurrentLayout) {
        this.$store.dispatch('sendAction', action);
      }

      if (this.isCurrentSampleLayout) {
        this.$store.dispatch('sendPreviewAction', action);
      }
    },

    updateField(field, value) {
      this.$emit('value', { field, value });
    },

    validateNumericInput(event) {
      const numericValue = event.target.value.replace(/[^0-9]/g, '');
      this.widgetId = numericValue;
      event.target.value = numericValue; // eslint-disable-line no-param-reassign
    },

    updateScorestreamUrl() {
      if (!this.isScorestream) {
        return;
      }

      const { widgetType, widgetId } = this;

      if (widgetType && widgetId && this.isValidWidgetId) {
        const baseUrl = process.env.GRAPHICS_SPA || 'https://graphic-builder.tagboard.com';
        const url = `${baseUrl}/scorestream.html?widgetType=${widgetType}&widgetId=${widgetId}`;
        this.updateField('url', url);
      }
    },

    selectAsset() {
      this.$store.dispatch('toggleAssetModal', { toggled: true, type: 'image' });

      window.assetmanfn = (asset, meta = {}) => {
        this.element.backgroundImageUrl = asset.media_url; // eslint-disable-line vue/no-mutating-props
        this.element.backgroundImageMeta = meta; // eslint-disable-line vue/no-mutating-props
        this.updateField('backgroundImageUrl', asset.media_url);
        this.updateField('backgroundImageMeta', meta);
        this.$store.dispatch('toggleAssetModal', { toggled: false, type: 'image' });
      };
    },
  },
};
</script>
